import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IStatic, ITravelHistory } from '@share/common-types';
import { AppThunk, Toaster, isSpanish } from '@share/utils';
import { ReservationsAPI, TravelHistoryAPI } from '@share/api';

export interface ITravelHistoryState {
  loading: boolean;
  error: string;
  result: {
    histories: ITravelHistory[];
    details: any;
    staticData: IStatic;
  }
}

const initialState: ITravelHistoryState = {
  loading: true,
  error: '',
  result: {
    histories: [],
    details: null,
    staticData: null
  }
};

const travelHistorySlice = createSlice({
  name: 'travelHistory',
  initialState,
  reducers: {
    setLoading: (state: ITravelHistoryState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setError: (state: ITravelHistoryState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setTravelHistory: (state: ITravelHistoryState, { payload }: PayloadAction<ITravelHistory[]>) => {
      state.result.histories = payload;
    },
    setTravelHistoryDetails: (state: ITravelHistoryState, { payload }: PayloadAction<any>) => {
      state.result.details = payload;
    },
    setTravelHistoryDetailsStatic: (state: ITravelHistoryState, { payload }: PayloadAction<IStatic>) => {
      state.result.staticData = payload;
    },
  },
});

export const travelHistoryActions = travelHistorySlice.actions;

export const travelHistoryReducer = travelHistorySlice.reducer;

export const getTravelHistory = (): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(travelHistoryActions.setLoading(true));
    dispatch(travelHistoryActions.setTravelHistory([]));

    try {
      const { loginStore } = getState();
      const { user } = loginStore;

      const response = await TravelHistoryAPI.get(user.keyid);

      dispatch(travelHistoryActions.setLoading(false));

      if (response.status === 200) {
        const { data } = response;

        dispatch(travelHistoryActions.setTravelHistory([...data.map((d: any) => {
          return {
            ...d,
            children: d?.children?.length ? [...d?.children] : undefined
          };
        })]));
      } else {
        dispatch(travelHistoryActions.setError(`[${response.status}] ${response.statusText}`));
         Toaster.error(!isSpanish() ? `An error occurred. [${response.status}] ${response.statusText}. If the problem persists, please contact the system administrator.`
                    : `Ha ocurrido un error. [${response.status}] ${response.statusText}. Si el problema persiste, por favor contacte al administrador del sistema.`);	
      }
    } catch (error) {
      console.error(error);
      dispatch(travelHistoryActions.setError(error.toString()));
      dispatch(travelHistoryActions.setLoading(false));
      Toaster.error(`An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`);
    }
  };
};

export const getTravelHistoryDetails = (confirmationNumber: string, callback: () => void): AppThunk => {
  return async (dispatch) => {
    dispatch(travelHistoryActions.setLoading(true));

    try {
      const response = await TravelHistoryAPI.getById(confirmationNumber);
      if (response.status === 200) {
        const { data } = response || {};
        if (data?.status) {
          dispatch(travelHistoryActions.setTravelHistoryDetails(data));
          dispatch(getTravelHistoryStatic(data.propertyId));
          callback?.();
        } else {
          Toaster.error(!isSpanish() ?'Reservation not found. We couldn’t locate a reservation. Try again later. If the problem persist, contact our support team.' :
           `Reserva no encontrada. No pudimos localizar una reserva. Inténtelo de nuevo más tarde. Si el problema persiste, contacte a nuestro equipo de soporte.`);
        }
      } else {
        dispatch(travelHistoryActions.setError(`[${response.status}] ${response.statusText}`));
        Toaster.error(`An error occurred. [${response.status}] ${response.statusText}. If the problem persists, please contact the system administrator.`);
      }
    } catch (error) {
      console.error(error);
      dispatch(travelHistoryActions.setError(error.toString()));
      dispatch(travelHistoryActions.setLoading(false));
      Toaster.error(`An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`);
    }

    dispatch(travelHistoryActions.setLoading(false));
  }
}

const getTravelHistoryStatic = (id: number): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(travelHistoryActions.setTravelHistoryDetailsStatic(null));
      const { data } = await ReservationsAPI.getStatic(id);

      dispatch(travelHistoryActions.setTravelHistoryDetailsStatic(data));

    } catch (error) {
      console.error(error);
      dispatch(travelHistoryActions.setError(error.toString()));
      Toaster.error(`An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`);
    }
  };
};
